import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { user_Login } from "../../component/services/UserApiServices/UserApiCalls";
import { getLogin, login_Staff } from "../../component/services/ApiCalls";
import * as SERVICE from "../../component/services/index";
import { AppStoreContext } from "../../store/AppStore";
import "../sass/MottruckLanding.scss";
import "./ContactDemo.scss";

/**
 * Shared Mottruck login UI.
 * variant:
 *  - user  → /user_login  (Shipper, Carrier, Client — and Staff if found)
 *  - admin → /login       (Admin)
 *  - staff → /staffLogin  (Staff)
 */
const Login = ({ variant = "user" }) => {
  const navigate = useNavigate();
  const { setAccess, setUserDetails, setIsLoading } = useContext(AppStoreContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const copy =
    variant === "admin"
      ? {
          title: "Welcome back.",
          lead: null,
          bullets: [],
          emailLabel: "Email or account number",
          emailPlaceholder: "admin@mottruck.com",
          hint: null,
          forgotTo: "/admin_forgotPassword",
        }
      : variant === "staff"
        ? {
            title: "Welcome back.",
            lead: null,
            bullets: [],
            emailLabel: "Email or account number",
            emailPlaceholder: "staff@mottruck.com",
            hint: null,
            forgotTo: "/staff_forgotPassword",
          }
        : {
            title: "Welcome back.",
            lead: null,
            bullets: [],
            emailLabel: "Email",
            emailPlaceholder: "name@company.com",
            hint: (
              <>
                You don&apos;t have an account? <Link to="/join">Join</Link>
              </>
            ),
            forgotTo: "/forget_password",
          };

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, [setIsLoading]);

  const validate = () => {
    let errors = 0;
    if (variant === "user") {
      const emailValidation = SERVICE.validateEmail(email);
      if (emailValidation !== undefined) {
        errors++;
        setEmailError(
          emailValidation === "required"
            ? "Please enter your email address."
            : "Please enter a valid email address."
        );
      } else {
        setEmailError(null);
      }
    } else if (!email.trim()) {
      errors++;
      setEmailError("Please enter your email address or account number.");
    } else {
      setEmailError(null);
    }

    if (!password) {
      errors++;
      setPasswordError("Please enter your password.");
    } else {
      setPasswordError(null);
    }
    return errors;
  };

  const resolveAccess = (role) => {
    if (role === "Carrier") return "carrierAccess";
    if (role === "Client") return "clientAccess";
    if (role === "Staff") return "staffAccess";
    if (role === "Admin") return "adminAccess";
    return "userAccess";
  };

  const persistSession = (data, token, access) => {
    localStorage.setItem("userDetail", JSON.stringify(data));
    if (token != null) localStorage.setItem("token", JSON.stringify(token));
    localStorage.setItem("access", JSON.stringify(access));
    setAccess(access);
    setUserDetails(data);
  };

  const doLogin = async () => {
    setIsSubmitting(true);
    try {
      if (variant === "admin") {
        const res = await getLogin({ email, password });
        if (res?.status === true) {
          persistSession(res.data, res.token, "adminAccess");
          toast.success("Login successfully");
          navigate("/account", { replace: true });
        } else {
          toast.error(res?.msg || "Invalid email or password");
        }
        return;
      }

      if (variant === "staff") {
        const res = await login_Staff({ email, password });
        if (res?.status === true) {
          persistSession(res.data, res.token, "staffAccess");
          toast.success("Login successfully");
          navigate("/account", { replace: true });
        } else {
          toast.error(res?.msg || "Invalid email or password");
        }
        return;
      }

      // Public: shipper / carrier / client (+ staff if present in API)
      const res = await user_Login({
        userEmail: email.trim().toLowerCase(),
        userPassword: password,
        type: "User",
      });
      if (res.status === true) {
        const role = res.data?.profile;
        const access = resolveAccess(role);
        persistSession(res.data, res.token, access);
        toast.success("Login successfully");
        navigate("/account", { replace: true });
      } else {
        toast.error(res.msg || "Invalid email or password");
      }
    } catch (err) {
      console.error("Login error:", err);
      toast.error("Please check your email and password");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (validate() > 0) return;
    await doLogin();
  };

  return (
    <div className={`mtk-landing mtk-split-page mtk-login-page mtk-login-${variant}`}>
      <section className="mtk-split-section">
        <div className="mtk-split-grid">
          <div className="mtk-split-left mtk-login-left">
            <h1>{copy.title}</h1>
            {copy.lead ? <p className="mtk-login-lead">{copy.lead}</p> : null}
            {copy.bullets.length > 0 ? (
              <div className="mtk-login-bullets">
                {copy.bullets.map((t) => (
                  <div key={t} className="bullet">
                    <span className="dot" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="mtk-split-right mtk-login-right">
            <form className="mtk-login-form" onSubmit={onSubmit}>
              <div className="field">
                <label>{copy.emailLabel}</label>
                <input
                  type={variant === "user" ? "email" : "text"}
                  placeholder={copy.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                />
                {emailError && submitted ? (
                  <small className="err">{emailError}</small>
                ) : null}
              </div>

              <div className="field">
                <label>Password</label>
                <div className="pwd-wrap">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="pwd-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                {passwordError && submitted ? (
                  <small className="err">{passwordError}</small>
                ) : null}
              </div>

              <div className="mtk-login-forgot">
                <Link to={copy.forgotTo}>Forgot password ?</Link>
              </div>

              <button type="submit" className="mtk-login-submit" disabled={isSubmitting}>
                {isSubmitting ? "Signing in…" : "Log in →"}
              </button>

              {copy.hint ? (
                <div className="mtk-login-hint">{copy.hint}</div>
              ) : null}
            </form>
          </div>
        </div>
      </section>
      <ToastContainer />
    </div>
  );
};

export default Login;
